var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { FieldButton, FormField, FormGrid, Page } from '@dixa/lib-shared-habla';
import { ApiErrorStatusCode } from 'lib-shared-api-utils';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ratingApi } from 'apis/rating';
import { AppLink } from 'components/appLink';
import { HookFormText } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { isQueryError } from 'utils/apis/axios';
import { validateName } from 'utils/formValidator';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useAppPathParams } from 'utils/url/useAppPathParams';
import { SurveyLanguageDropdown } from './components/languages/components/dropdown/surveyLanguagesDropdown';
import { SurveyLanguagesTable } from './components/languages/components/table/surveyLanguagesTable';
import styles from './survey.module.scss';
export const SatisfactionSurveyView = () => {
    const { settingsId } = useAppPathParams('/settings/satisfaction/phone/survey/:settingsId');
    const notify = useNotifications();
    const navigate = useAppNavigate();
    const { data: surveyRatingSettings } = ratingApi.useGetRatingSettingsQuery({
        ratingSettingsId: settingsId,
    });
    const { data: languageRatingSettings, isSuccess: listLanguageSettingIsSuccess, } = ratingApi.useReadRatingSettingsQuery({
        level: 'language',
        surveyType: 'text_to_speech',
        parentId: settingsId,
    });
    const [updateRatingSettings] = ratingApi.useLazyUpdateRatingSettingsQuery();
    const handleGoToLanguage = useCallback((languageCode) => {
        navigate({
            path: `/settings/satisfaction/phone/survey/:settingsId/:languageCode`,
            params: {
                path: { settingsId: settingsId, languageCode: languageCode },
            },
        });
    }, [navigate, settingsId]);
    const form = useForm({
        mode: 'all',
    });
    // Finishes initializing after the data has been fetched.
    useEffect(() => {
        var _a;
        if (listLanguageSettingIsSuccess) {
            form.setValue('name', (_a = surveyRatingSettings === null || surveyRatingSettings === void 0 ? void 0 : surveyRatingSettings.data.name) !== null && _a !== void 0 ? _a : '');
        }
    }, [form, listLanguageSettingIsSuccess, surveyRatingSettings === null || surveyRatingSettings === void 0 ? void 0 : surveyRatingSettings.data.name]);
    const handleValidSubmit = (values) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const updateReq = {
                id: settingsId,
                name: values.name,
            };
            yield updateRatingSettings(updateReq).unwrap();
            notify({
                title: 'Success',
                text: `The survey has been updated.`,
                icon: 'done',
                time: 5000,
            }, NotificationType.Success);
            form.reset({}, { keepValues: true });
        }
        catch (error) {
            const title = error &&
                isQueryError(error) &&
                error.status === ApiErrorStatusCode.Conflict
                ? 'Survey name already exists.'
                : 'Error while saving the survey.';
            notify({
                title,
            }, NotificationType.Error);
        }
    });
    return (_jsxs(_Fragment, { children: [_jsx(Page.Section, Object.assign({ titleMeta: _jsx(AppLink, Object.assign({ path: "/settings/satisfaction/phone" }, { children: "\u2190 Back to survey list" })) }, { children: _jsx("form", Object.assign({ noValidate: true, tabIndex: -1, onSubmit: form.handleSubmit(handleValidSubmit) }, { children: _jsxs(FormGrid, { children: [_jsx(FormField, Object.assign({ span: 2 }, { children: _jsx(HookFormText, { name: "name", label: "CSAT survey name", control: form.control, rules: {
                                        required: 'Name is required',
                                        validate: validateName,
                                    }, componentProps: {
                                        autoFocus: true,
                                        placeholder: 'e.g. Lalalala',
                                    } }) })), _jsx(FormField, Object.assign({ span: 1 }, { children: _jsx(FieldButton, Object.assign({ variant: "primary", className: styles.inputSurveyName, disabled: !form.formState.isDirty ||
                                        !form.formState.isValid ||
                                        form.formState.isSubmitting, type: "submit" }, { children: "Save" })) }))] }) })) })), _jsx(Page.Section, Object.assign({ title: "Language options", controls: _jsx(SurveyLanguageDropdown, { handleGoToLanguage: handleGoToLanguage, languageSettings: languageRatingSettings
                        ? languageRatingSettings.data.rating_settings
                        : [] }) }, { children: _jsx(SurveyLanguagesTable, { handleGoToLanguage: handleGoToLanguage, surveyRatingSettingsId: settingsId }) }))] }));
};
